import { fail, ok, type Result } from './result.js';
import { nomeSemControle } from './vo/texto.js';

/**
 * Modelo próprio (03/11, ficha `demandas/processadas/2026-11-03_malgo_VIS_modos-gelo-mare-e-editor.md`): o administrador edita as
 * cores a partir de um dos modos e aplica o resultado no workspace. Uma regra só para a tela e a API: lista de tokens, hex
 * validado, contraste AA e o CSS montado. Nome de token e hex são as únicas coisas que entram no CSS; nenhum texto livre passa.
 */
export const TOKENS_PAGINA = ["fundo", "superficie-1", "superficie-2", "superficie-3", "tinta-1", "tinta-2", "tinta-3", "borda", "borda-campo", "campo", "foco", "acento", "acento-forte", "acento-hover", "acento-tinta", "azul", "verde", "perigo", "dourado", "laranja", "apoio"] as const;
export const TOKENS_BARRA = ["fundo", "superficie-2", "superficie-3", "tinta-1", "tinta-2", "tinta-3", "borda", "borda-campo", "campo", "acento-forte"] as const;
export type TokenPagina = (typeof TOKENS_PAGINA)[number];
export type TokenBarra = (typeof TOKENS_BARRA)[number];

export interface CoresTemaModelo {
  pagina: Record<TokenPagina, string>;
  barra: Record<TokenBarra, string>;
}
export const BASES_DE_MODELO = ['mare_malgo', 'gelo', 'malgo'] as const;
export type BaseDeModelo = (typeof BASES_DE_MODELO)[number];

export interface ModeloTema {
  id: string;
  nome: string;
  base: BaseDeModelo;
  claro: CoresTemaModelo;
  escuro: CoresTemaModelo;
}

export const LIMITE_MODELOS = 12;
/** O modelo que está sendo salvo precisa ser legível nos dois temas. */
export function validarContrasteModelo(m: Pick<ModeloTema, 'nome' | 'claro' | 'escuro'>): Result<void> {
  if (falhasDeContraste(m.claro).length || falhasDeContraste(m.escuro).length) return fail('modelo.contraste', `O modelo ${m.nome} tem combinações de cor sem contraste suficiente`, { campo: 'modelos' });
  return ok(undefined);
}

/** Só os modelos com formato válido: um deles fora de formato não pode derrubar a aparência inteira. */
export function modelosAproveitaveis(lista: unknown): ModeloTema[] {
  if (!Array.isArray(lista)) return [];
  const ids = new Set<string>();
  const nomes = new Set<string>();
  return lista.slice(0, LIMITE_MODELOS).flatMap((m) => {
    const r = validarModelosTema([completarModelo(m as Partial<ModeloTema>)]);
    if (!r.ok) return [];
    const { id, nome } = r.valor[0]!;
    const chave = nome.toLocaleLowerCase('pt-BR');
    if (ids.has(id) || nomes.has(chave)) return [];
    ids.add(id);
    nomes.add(chave);
    return r.valor;
  });
}

/** Token que faltar no modelo gravado (lista nova de tokens) vem do modo de partida: o modelo não some por isso (rcor-3 ROB3-B1). */
function completarModelo(m: Partial<ModeloTema>): Partial<ModeloTema> {
  if (typeof m?.base !== 'string' || !Object.hasOwn(CORES_DAS_BASES, m.base)) return m;
  const base = CORES_DAS_BASES[m.base as BaseDeModelo];
  const parte = (lista: readonly string[], padrao: Record<string, string>, gravado: unknown) =>
    Object.fromEntries(lista.map((k) => [k, (gravado as Record<string, string> | undefined)?.[k] ?? padrao[k]!]));
  const tema = (t: 'claro' | 'escuro') => ({ pagina: parte(TOKENS_PAGINA, base[t].pagina, m[t]?.pagina), barra: parte(TOKENS_BARRA, base[t].barra, m[t]?.barra) }) as CoresTemaModelo;
  return { ...m, claro: tema('claro'), escuro: tema('escuro') };
}
export const ID_ESTILO_MODELO = 'malgoos-modelo';
export const RE_HEX_MODELO = /^#[0-9a-f]{6}$/i;
const RE_ID_MODELO = /^m[0-9a-f]{8}$/;

/** Cores de partida de cada modo, nos dois temas: os mesmos valores de tokens.css e estilos.css. */
export const CORES_DAS_BASES = {
  "mare_malgo": {
    "claro": {
      "pagina": {
        "fundo": "#eef6f5",
        "superficie-1": "#ffffff",
        "superficie-2": "#e9f3f1",
        "superficie-3": "#d8e9e6",
        "tinta-1": "#0c2b28",
        "tinta-2": "#2d4d49",
        "tinta-3": "#476662",
        "borda": "#cfe2de",
        "borda-campo": "#5f7f7a",
        "campo": "#ffffff",
        "foco": "#0c2b28",
        "acento": "#00786b",
        "acento-forte": "#00665b",
        "acento-hover": "#00665b",
        "acento-tinta": "#ffffff",
        "azul": "#00665b",
        "verde": "#14673a",
        "perigo": "#b42318",
        "dourado": "#6f5400",
        "laranja": "#8e1b12",
        "apoio": "#00665b"
      },
      "barra": {
        "fundo": "#00473f",
        "superficie-2": "#004f46",
        "superficie-3": "#006358",
        "tinta-1": "#ffffff",
        "tinta-2": "#e3f3f0",
        "tinta-3": "#b9dcd6",
        "borda": "#1a6b60",
        "borda-campo": "#8ccbc1",
        "campo": "#004f46",
        "acento-forte": "#f4e300"
      }
    },
    "escuro": {
      "pagina": {
        "fundo": "#0f1c18",
        "superficie-1": "#16261f",
        "superficie-2": "#1d3029",
        "superficie-3": "#263b34",
        "tinta-1": "#eef3ef",
        "tinta-2": "#c4d1ca",
        "tinta-3": "#a3b4ab",
        "borda": "#2b423a",
        "borda-campo": "#74897e",
        "campo": "#1d3029",
        "foco": "#eef3ef",
        "acento": "#74b957",
        "acento-forte": "#74b957",
        "acento-hover": "#88c86c",
        "acento-tinta": "#0f241e",
        "azul": "#4cd3c2",
        "verde": "#8fd673",
        "perigo": "#f39a8f",
        "dourado": "#f4e300",
        "laranja": "#f8bdb6",
        "apoio": "#4cd3c2"
      },
      "barra": {
        "fundo": "#0b1613",
        "superficie-2": "#172822",
        "superficie-3": "#1b3f35",
        "tinta-1": "#eef3ef",
        "tinta-2": "#e6eee8",
        "tinta-3": "#a9bab1",
        "borda": "#22362f",
        "borda-campo": "#74897e",
        "campo": "#172822",
        "acento-forte": "#74b957"
      }
    }
  },
  "gelo": {
    "claro": {
      "pagina": {
        "fundo": "#f4f6f4",
        "superficie-1": "#ffffff",
        "superficie-2": "#eef2ee",
        "superficie-3": "#e0e8e1",
        "tinta-1": "#12261f",
        "tinta-2": "#3c5148",
        "tinta-3": "#54685f",
        "borda": "#d5ded7",
        "borda-campo": "#73857c",
        "campo": "#ffffff",
        "foco": "#12261f",
        "acento": "#1b3f35",
        "acento-forte": "#1b3f35",
        "acento-hover": "#27574a",
        "acento-tinta": "#ffffff",
        "azul": "#00705f",
        "verde": "#14673a",
        "perigo": "#b42318",
        "dourado": "#6f5400",
        "laranja": "#8e1b12",
        "apoio": "#00705f"
      },
      "barra": {
        "fundo": "#e0e8e1",
        "superficie-2": "#d2ddd4",
        "superficie-3": "#ffffff",
        "tinta-1": "#12261f",
        "tinta-2": "#1b3f35",
        "tinta-3": "#34493f",
        "borda": "#c3d0c6",
        "borda-campo": "#5f7168",
        "campo": "#ffffff",
        "acento-forte": "#1b3f35"
      }
    },
    "escuro": {
      "pagina": {
        "fundo": "#0f1c18",
        "superficie-1": "#16261f",
        "superficie-2": "#1d3029",
        "superficie-3": "#263b34",
        "tinta-1": "#eef3ef",
        "tinta-2": "#c4d1ca",
        "tinta-3": "#a3b4ab",
        "borda": "#2b423a",
        "borda-campo": "#74897e",
        "campo": "#1d3029",
        "foco": "#eef3ef",
        "acento": "#74b957",
        "acento-forte": "#74b957",
        "acento-hover": "#88c86c",
        "acento-tinta": "#0f241e",
        "azul": "#4cd3c2",
        "verde": "#8fd673",
        "perigo": "#f39a8f",
        "dourado": "#f4e300",
        "laranja": "#f8bdb6",
        "apoio": "#4cd3c2"
      },
      "barra": {
        "fundo": "#0b1613",
        "superficie-2": "#172822",
        "superficie-3": "#1b3f35",
        "tinta-1": "#eef3ef",
        "tinta-2": "#e6eee8",
        "tinta-3": "#a9bab1",
        "borda": "#22362f",
        "borda-campo": "#74897e",
        "campo": "#172822",
        "acento-forte": "#74b957"
      }
    }
  },
  "malgo": {
    "claro": {
      "pagina": {
        "fundo": "#f1f2f2",
        "superficie-1": "#ffffff",
        "superficie-2": "#e7ecea",
        "superficie-3": "#dce3e0",
        "tinta-1": "#11203a",
        "tinta-2": "#40506c",
        "tinta-3": "#51617c",
        "borda": "#c9d1de",
        "borda-campo": "#6e7b90",
        "campo": "#ffffff",
        "foco": "#11203a",
        "acento": "#1b3f35",
        "acento-forte": "#1b3f35",
        "acento-hover": "#285145",
        "acento-tinta": "#ffffff",
        "azul": "#0060c1",
        "verde": "#0d703a",
        "perigo": "#b42318",
        "dourado": "#8a6000",
        "laranja": "#a34100",
        "apoio": "#01786b"
      },
      "barra": {
        "fundo": "#1b3f35",
        "superficie-2": "#264c41",
        "superficie-3": "#2f574c",
        "tinta-1": "#f5f5f5",
        "tinta-2": "#dddddd",
        "tinta-3": "#c2cec9",
        "borda": "#375b50",
        "borda-campo": "#8da59b",
        "campo": "#264c41",
        "acento-forte": "#94da77"
      }
    },
    "escuro": {
      "pagina": {
        "fundo": "#0e1815",
        "superficie-1": "#16221f",
        "superficie-2": "#1d2b27",
        "superficie-3": "#253430",
        "tinta-1": "#f2f2f2",
        "tinta-2": "#cad4d0",
        "tinta-3": "#a3b2ac",
        "borda": "#2a4039",
        "borda-campo": "#75847e",
        "campo": "#1d2b27",
        "foco": "#f2f2f2",
        "acento": "#74b957",
        "acento-forte": "#74b957",
        "acento-hover": "#84ca67",
        "acento-tinta": "#0f241e",
        "azul": "#59a0f9",
        "verde": "#7bd15c",
        "perigo": "#e08c84",
        "dourado": "#e7bf4a",
        "laranja": "#f6922e",
        "apoio": "#2ec7b6"
      },
      "barra": {
        "fundo": "#1b3f35",
        "superficie-2": "#264c41",
        "superficie-3": "#2f574c",
        "tinta-1": "#f5f5f5",
        "tinta-2": "#dddddd",
        "tinta-3": "#c2cec9",
        "borda": "#375b50",
        "borda-campo": "#8da59b",
        "campo": "#264c41",
        "acento-forte": "#94da77"
      }
    }
  }
} as Record<BaseDeModelo, { claro: CoresTemaModelo; escuro: CoresTemaModelo }>;

function canal(v: number) {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function luminancia(hex: string) {
  const n = Number.parseInt(hex.slice(1), 16);
  return 0.2126 * canal((n >> 16) & 255) + 0.7152 * canal((n >> 8) & 255) + 0.0722 * canal(n & 255);
}
/** Razão de contraste WCAG entre dois hex. */
export function contrasteHex(a: string, b: string) {
  const x = luminancia(a);
  const y = luminancia(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

const canais = (h: string) => [1, 3, 5].map((i) => Number.parseInt(h.slice(i, i + 2), 16));
function misturarHex(frente: string, fundo: string, alfa: number) {
  const b = canais(fundo);
  return `#${canais(frente)
    .map((v, i) => Math.round(v * alfa + b[i]! * (1 - alfa)).toString(16).padStart(2, '0'))
    .join('')}`;
}

export interface FalhaContraste {
  area: 'pagina' | 'barra';
  frente: string;
  fundo: string;
  razao: number;
  minimo: number;
  /** O par é o selo tingido, não a cor direto sobre o fundo. */
  selo?: boolean;
}

const AA = 4.5;
const INDICADOR = 3;

/** Os pares que precisam ser legíveis. Texto em AA (4,5:1); borda de campo e foco como indicador (3:1). */
export function falhasDeContraste(c: CoresTemaModelo): FalhaContraste[] {
  const f: FalhaContraste[] = [];
  const par = (area: 'pagina' | 'barra', cores: Record<string, string>, frente: string, fundo: string, minimo: number) => {
    const razao = contrasteHex(cores[frente]!, cores[fundo]!);
    if (razao < minimo) f.push({ area, frente, fundo, razao, minimo });
  };
  for (const fundo of ['fundo', 'superficie-1', 'superficie-2', 'superficie-3']) {
    for (const t of ['tinta-1', 'tinta-2', 'tinta-3']) par('pagina', c.pagina, t, fundo, AA);
    par('pagina', c.pagina, 'foco', fundo, INDICADOR);
  }
  for (const fundo of ['fundo', 'superficie-1']) for (const t of ['acento-forte', 'azul', 'verde', 'perigo', 'dourado', 'laranja', 'apoio']) par('pagina', c.pagina, t, fundo, AA);
  // Selo, como em `Selo` (componentes/base): a própria cor sobre o tingido de 8% dela; o de atenção, sobre o amarelo de realce a 22%.
  for (const fundo of ['superficie-1', 'superficie-2'] as const) {
    for (const cor of ['azul', 'laranja', 'verde', 'perigo'] as const) {
      const razao = contrasteHex(c.pagina[cor], misturarHex(c.pagina[cor], c.pagina[fundo], 0.08));
      if (razao < AA) f.push({ area: 'pagina', frente: cor, fundo, razao, minimo: AA, selo: true });
    }
    const razao = contrasteHex(c.pagina.dourado, misturarHex('#f4e300', c.pagina[fundo], 0.22));
    if (razao < AA) f.push({ area: 'pagina', frente: 'dourado', fundo, razao, minimo: AA, selo: true });
  }
  par('pagina', c.pagina, 'acento-tinta', 'acento', AA);
  par('pagina', c.pagina, 'acento-tinta', 'acento-hover', AA);
  par('pagina', c.pagina, 'tinta-1', 'campo', AA);
  par('pagina', c.pagina, 'borda-campo', 'superficie-1', INDICADOR);
  par('pagina', c.pagina, 'borda-campo', 'campo', INDICADOR);
  for (const fundo of ['fundo', 'superficie-2', 'superficie-3']) for (const t of ['tinta-1', 'tinta-2', 'tinta-3', 'acento-forte']) par('barra', c.barra, t, fundo, AA);
  par('barra', c.barra, 'tinta-1', 'campo', AA);
  par('barra', c.barra, 'borda-campo', 'fundo', INDICADOR);
  return f;
}

function validarCores(c: unknown): Result<CoresTemaModelo> {
  const v = c as Partial<CoresTemaModelo> | null;
  const conferir = (lista: readonly string[], o: Record<string, unknown> | undefined) => !!o && Object.keys(o).length === lista.length && lista.every((k) => typeof o[k] === 'string' && RE_HEX_MODELO.test(o[k]));
  if (!conferir(TOKENS_PAGINA, v?.pagina) || !conferir(TOKENS_BARRA, v?.barra)) return fail('modelo.cor_invalida', 'Use cores em hexadecimal, como #1B3F35', { campo: 'modelos' });
  return ok(v as CoresTemaModelo);
}

/**
 * Valida a lista: formato, nomes únicos e limite. O contraste é de quem está salvando (`validarContrasteModelo`): modelo já
 * gravado não some nem bloqueia o resto se a regra de contraste ficar mais rígida (rcor-2 ROB2-M1).
 */
export function validarModelosTema(lista: unknown): Result<ModeloTema[]> {
  if (!Array.isArray(lista) || lista.length > LIMITE_MODELOS) return fail('modelo.limite', `Até ${LIMITE_MODELOS} modelos`, { campo: 'modelos' });
  const saida: ModeloTema[] = [];
  const ids = new Set<string>();
  const nomes = new Set<string>();
  for (const m of lista as Partial<ModeloTema>[]) {
    if (typeof m?.id !== 'string' || !RE_ID_MODELO.test(m.id) || ids.has(m.id)) return fail('modelo.id_invalido', 'Modelo inválido', { campo: 'modelos' });
    const nome = nomeSemControle('nome', String(m.nome ?? ''), 40);
    if (!nome.ok) return nome;
    const chave = nome.valor.toLocaleLowerCase('pt-BR');
    if (nomes.has(chave)) return fail('modelo.nome_repetido', `Já existe um modelo chamado ${nome.valor}`, { campo: 'modelos' });
    if (!(BASES_DE_MODELO as readonly string[]).includes(String(m.base))) return fail('modelo.base_invalida', 'Modo de partida inválido', { campo: 'modelos' });
    const claro = validarCores(m.claro);
    if (!claro.ok) return claro;
    const escuro = validarCores(m.escuro);
    if (!escuro.ok) return escuro;
    ids.add(m.id);
    nomes.add(chave);
    saida.push({ id: m.id, nome: nome.valor, base: m.base as BaseDeModelo, claro: claro.valor, escuro: escuro.valor });
  }
  return ok(saida);
}

const declaracoes = (o: Record<string, string>) =>
  Object.entries(o)
    .filter(([k, v]) => /^[a-z0-9-]{1,24}$/.test(k) && RE_HEX_MODELO.test(v))
    .map(([k, v]) => `--${k}:${v};`)
    .join('');

/** Variáveis da página e da barra para a prévia do editor (a tela aplica como `style`, dentro do cartão). */
export const variaveisPagina = (c: CoresTemaModelo) => ({ ...Object.fromEntries(Object.entries(c.pagina).map(([k, v]) => [`--${k}`, v])), '--acento-suave': `${c.pagina.acento}1f` });
export const variaveisBarra = (c: CoresTemaModelo) => ({
  ...Object.fromEntries(Object.entries(c.barra).map(([k, v]) => [`--${k}`, v])),
  '--superficie-1': c.barra.fundo,
  '--foco': c.barra['tinta-1'],
  '--acento': c.barra['acento-forte'],
  '--acento-hover': c.barra['acento-forte'],
  '--acento-suave': `${c.barra['acento-forte']}29`,
});

/**
 * CSS do modelo nos dois temas. `html:root:root` ganha dos blocos de reserva de estilos.css (inclusive os com `:not([data-acento])`), em qualquer ordem no <head>
 * (o `tema.js` injeta antes da folha de estilos carregar). Só nome de token e hex validados entram.
 */
export function cssDoModelo(m: Pick<ModeloTema, 'claro' | 'escuro'>) {
  return (['claro', 'escuro'] as const)
    .map((t) => {
      const c = m[t];
      if (!validarCores(c).ok) return '';
      const h = `html:root:root[data-estilo='proprio'][data-tema='${t}']`;
      return `${h}{${declaracoes(c.pagina)}--acento-suave:${c.pagina.acento}1f;}${h} .superficie-marca,${h} [data-menu-lateral],${h} [data-gaveta]{${declaracoes(c.barra)}--superficie-1:${c.barra.fundo};--foco:${c.barra['tinta-1']};--acento:${c.barra['acento-forte']};--acento-hover:${c.barra['acento-forte']};--acento-suave:${c.barra['acento-forte']}29;color:var(--tinta-1);}`;
    })
    .join('');
}
